import { MatterBannerKind } from '@/matter/store/schemas'
import { useContext, useState } from 'react'

import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { Button } from '@/components/button/Button'
import { AuthContext } from '@/context/auth-context'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { SelectExternalFolderModal } from '@/user_files/external-storage/components/SelectExternalFolderModal'
import { useDriveSyncSetupBanner } from '@/user_files/external-storage/hooks/useDriveSyncSetupBanner'

type NoSyncedFolderEmptyStateProps = {
  matterId: string
}

/**
 * What the Drive View's DMS source shows when the account is connected but no folder is synced to
 * this matter yet — the source toggle is reachable in that state, so it needs something to show
 * besides an empty file table. Renders nothing when there is no folder left to connect.
 */
export function NoSyncedFolderEmptyState({ matterId }: NoSyncedFolderEmptyStateProps) {
  const { trackEvent } = useAnalytics()
  const authContext = useContext(AuthContext)
  const { kind, connectedAccount, mapping } = useDriveSyncSetupBanner(matterId)
  const [selectFolderOpen, setSelectFolderOpen] = useState(false)

  if (kind !== MatterBannerKind.DriveSyncConnectFolder || !connectedAccount) return null

  const ProviderIcon = PROVIDER_ICON_COMPONENTS[connectedAccount.provider]
  const providerLabel = PROVIDER_COMPACT_DISPLAY_NAME[connectedAccount.provider]

  const handleConnectFolderClick = () => {
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncSetupEntryClicked,
      {
        matterId,
        organizationId: getDriveSyncOrganizationId(authContext?.userAccountData),
        externalStorageProvider: connectedAccount.provider,
        matterDriveSyncEntrySource: MatterDriveSyncEntrySource.DriveView,
      },
      trackEvent
    )
    setSelectFolderOpen(true)
  }

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-brand-neutral-300 bg-brand-neutral-50 px-4 pb-6 pt-4">
        <div className="flex size-11 items-center justify-center rounded-md border border-brand-neutral-200 bg-white">
          <ProviderIcon className="h-[28px] w-[32px]" />
        </div>
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex flex-col items-center">
            <p className="font-rethink-sans text-base font-medium leading-6 text-brand-neutral-900">No folder synced yet</p>
            <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">Sync a {providerLabel} folder to this matter</p>
          </div>
          <Button variant="tonal" color="primary" size="sm" onClick={handleConnectFolderClick}>
            Connect Folder
          </Button>
        </div>
      </div>
      <SelectExternalFolderModal
        account={connectedAccount}
        currentExternalFolderId={mapping?.external_folder_id}
        matterId={matterId}
        open={selectFolderOpen}
        onOpenChange={setSelectFolderOpen}
        replaceExistingMapping={mapping != null}
        matterDriveSyncEntrySource={MatterDriveSyncEntrySource.DriveView}
      />
    </>
  )
}
